import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useGenerationToastOptional, previewFromNotification } from '../contexts/GenerationToastContext';
import { Database } from '../types/database.types';

type Notification = Database['public']['Tables']['notifications']['Row'];

const TYPE_ICONS: Record<string, string> = {
  request_created: '📋',
  request_approved: '✅',
  request_rejected: '❌',
  credits_low: '⚠️',
  marketplace_sale: '🛒',
  referral_completed: '🎁',
  member_invited: '👤',
  generation_completed: '🎬',
};

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const generationToast = useGenerationToastOptional();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    setNotifications(data || []);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    if (!user) return;
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as Notification;
          setNotifications((prev) => [row, ...prev.filter((n) => n.id !== row.id)].slice(0, 20));
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const markAllRead = async () => {
    if (!user) return;
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const openNotification = async (n: Notification) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', n.id);
    setNotifications(prev => prev.map(row => row.id === n.id ? { ...row, is_read: true } : row));
    setOpen(false);

    if (n.type === 'generation_completed' && generationToast) {
      const preview = previewFromNotification(n);
      if (preview) {
        generationToast.openPreview(preview);
        return;
      }
    }

    const href =
      (n.metadata && typeof n.metadata === 'object' && !Array.isArray(n.metadata)
        ? (n.metadata as { href?: string }).href
        : undefined) || (n.type === 'generation_completed' ? '/galeria' : null);
    if (href) navigate(href);
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="notification-wrapper" ref={ref}>
      <button
        type="button"
        className="notification-bell"
        onClick={() => setOpen(!open)}
        aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lida${unreadCount > 1 ? 's' : ''}` : 'Notificações'}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={20} aria-hidden />
        {unreadCount > 0 && <span className="notification-badge" aria-hidden>{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {/* Anuncia novas notificações sem roubar o foco */}
      <span className="sr-only" role="status" aria-live="polite">
        {unreadCount > 0 ? `${unreadCount} notificações não lidas` : ''}
      </span>

      {open && (
        <div className="notification-dropdown" role="region" aria-label="Notificações">
          <div className="notification-dropdown-header">
            <h3>Notificações</h3>
            {unreadCount > 0 && (
              <button type="button" className="btn-link-sm" onClick={markAllRead}><CheckCheck size={14} aria-hidden /> Marcar todas como lidas</button>
            )}
          </div>
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={22} aria-hidden />
                <strong>Tudo em dia</strong>
                <span>Avisos de gerações concluídas, pedidos e créditos aparecem aqui.</span>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  type="button"
                  key={n.id}
                  className={`notification-item ${!n.is_read ? 'unread' : ''}`}
                  onClick={() => void openNotification(n)}
                >
                  <span className="notification-icon" aria-hidden>{TYPE_ICONS[n.type] || '📌'}</span>
                  <span className="notification-content">
                    <span className="notification-title">{n.title}{!n.is_read && <span className="sr-only"> (não lida)</span>}</span>
                    {n.message && <span className="notification-msg">{n.message}</span>}
                    <span className="notification-time">{n.created_at ? timeAgo(n.created_at) : ''}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
